import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Row, Spinner, Table } from "react-bootstrap";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import {
  FaArrowRight,
  FaCheck,
  FaClock,
  FaFolderOpen,
  FaMobileAlt,
  FaTimes,
  FaUserCheck,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import AppShell from "../../components/AppShell";
import NotificationIcon from "../../components/Notification";
import PageHeader from "../../components/PageHeader";
import { useProjectContext } from "../../contextProviders/projectContext";
import "./AdminDashboard.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Tooltip,
  Legend
);

const BLUE = "#1B3A87";
const LINE = "#D8DFEC";
const INK = "#17213A";
const FONT = "Source Sans 3";

const formatNumber = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 1 });
const formatInteger = new Intl.NumberFormat("en-ZA", { maximumFractionDigits: 0 });

const MONTH_WINDOW = 12;

const buildMonthBuckets = (logs = []) => {
  const today = new Date();
  const months = Array.from({ length: MONTH_WINDOW }, (_, index) => {
    const date = new Date(today.getFullYear(), today.getMonth() - (MONTH_WINDOW - 1 - index), 1);
    return {
      key: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`,
      label: date.toLocaleDateString("en-ZA", { month: "short" }),
      hours: 0,
    };
  });

  const monthMap = new Map(months.map((month) => [month.key, month]));
  logs.forEach((log) => {
    if (log.status !== "approved") return;
    const date = new Date(log.startTime);
    if (Number.isNaN(date.getTime())) return;
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const month = monthMap.get(key);
    if (month) month.hours += Number(log.hours) || 0;
  });

  return months;
};

const baseChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 250 },
  plugins: {
    legend: { display: false },
    tooltip: {
      displayColors: false,
      titleFont: { family: FONT, weight: "500" },
      bodyFont: { family: FONT },
    },
  },
  scales: {
    x: {
      grid: { display: false },
      border: { color: LINE },
      ticks: { color: INK, font: { family: FONT, size: 12 } },
    },
    y: {
      beginAtZero: true,
      grid: { color: LINE },
      border: { display: false },
      ticks: { color: INK, precision: 0, font: { family: FONT, size: 12 } },
    },
  },
};

const horizontalChartOptions = {
  ...baseChartOptions,
  indexAxis: "y",
  scales: {
    x: {
      beginAtZero: true,
      grid: { color: LINE },
      border: { display: false },
      ticks: { color: INK, precision: 0, font: { family: FONT, size: 12 } },
    },
    y: {
      grid: { display: false },
      border: { color: LINE },
      ticks: { color: INK, font: { family: FONT, size: 12 } },
    },
  },
};

const Metric = ({ label, value, detail, icon: Icon }) => (
  <div className="admin-metric">
    <div className="admin-metric__copy">
      <span className="admin-metric__label">{label}</span>
      <strong className="admin-metric__value wcco-technical">{value}</strong>
      <span className="admin-metric__detail">{detail}</span>
    </div>
    <Icon className="admin-metric__icon" size={20} aria-hidden="true" />
  </div>
);

const SectionHeader = ({ title, description, action }) => (
  <div className="admin-section-header">
    <div>
      <h2>{title}</h2>
      {description && <p>{description}</p>}
    </div>
    {action}
  </div>
);

const EmptyState = ({ children }) => <div className="admin-empty-state">{children}</div>;

const AdminDashboard = () => {
  const {
    projects,
    loading: projectsLoading,
    error: projectsError,
    fetchProjects,
    updateProject,
  } = useProjectContext();
  const navigate = useNavigate();
  const [reportSummary, setReportSummary] = useState(null);
  const [pushStats, setPushStats] = useState(null);
  const [reportLoading, setReportLoading] = useState(true);
  const [reportError, setReportError] = useState("");
  const [actionProjectId, setActionProjectId] = useState("");

  const fetchReportingData = useCallback(async () => {
    try {
      setReportLoading(true);
      const [reportRes, pushRes] = await Promise.all([
        api.get("/reports/summary"),
        api.get("/push/stats").catch(() => ({ data: null })),
      ]);
      setReportSummary(reportRes.data);
      if (pushRes?.data) setPushStats(pushRes.data);
      setReportError("");
    } catch (error) {
      console.error("Error fetching dashboard reporting data:", error);
      setReportError("Could not load dashboard reporting data.");
    } finally {
      setReportLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReportingData();
  }, [fetchReportingData]);

  const handleApproveReject = async (projectId, isApproved) => {
    let feedback = "";
    if (!isApproved) {
      const response = window.prompt("Enter feedback for rejecting this project proposal:");
      if (response === null) return;
      feedback = response.trim();
    }

    try {
      setActionProjectId(projectId);
      await updateProject(projectId, { approved: isApproved, feedback });
      await Promise.all([fetchProjects(), fetchReportingData()]);
    } catch (error) {
      console.error("Error updating project approval status:", error);
      setReportError("Could not update the project approval. Please try again.");
    } finally {
      setActionProjectId("");
    }
  };

  const dashboard = useMemo(() => {
    if (!reportSummary) return null;

    const { metrics, logs = [], projects: projectRows = [], distributions } = reportSummary;
    const pendingProjects = projects.filter((project) => !project.approved && !project.feedback);
    const pendingLogs = logs.filter((log) => log.status === "pending");
    const approvedLogs = logs.filter((log) => log.status === "approved");
    const months = buildMonthBuckets(logs);
    const twelveMonthHours = months.reduce((sum, month) => sum + month.hours, 0);

    const topProjects = projectRows
      .filter((project) => project.approvedHours > 0)
      .slice()
      .sort((a, b) => b.approvedHours - a.approvedHours)
      .slice(0, 6);

    const pendingByProject = projectRows
      .filter((project) => project.pendingHours > 0)
      .slice()
      .sort((a, b) => b.pendingHours - a.pendingHours)
      .slice(0, 5);

    const faculties = (distributions.faculties || []).filter((item) => item.value > 0).slice(0, 6);
    const allInterests = (distributions.interests || []).filter((item) => item.value > 0);
    const interests = allInterests.slice(0, 6);
    const interestTotal = allInterests.reduce((sum, item) => sum + item.value, 0);
    const engagementRate = metrics.totalStudents
      ? (metrics.activeVolunteers / metrics.totalStudents) * 100
      : 0;
    const approvalRate = metrics.totalProjects
      ? (metrics.approvedProjects / metrics.totalProjects) * 100
      : 0;

    return {
      metrics,
      logs,
      pendingProjects,
      pendingLogs,
      approvedLogs,
      months,
      twelveMonthHours,
      topProjects,
      pendingByProject,
      faculties,
      interests,
      interestTotal,
      engagementRate,
      approvalRate,
    };
  }, [projects, reportSummary]);

  if (projectsLoading || reportLoading) {
    return (
      <AppShell>
        <div className="admin-dashboard-state" role="status">
          <Spinner animation="border" />
          <span>Loading dashboard data…</span>
        </div>
      </AppShell>
    );
  }

  if (projectsError || reportError || !dashboard) {
    return (
      <AppShell>
        <Alert>
          <strong>Dashboard unavailable.</strong> {projectsError || reportError || "No report data was returned."}
          <div className="mt-3">
            <Button onClick={fetchReportingData}>Try again</Button>
          </div>
        </Alert>
      </AppShell>
    );
  }

  const generatedAt = reportSummary.generatedAt
    ? new Date(reportSummary.generatedAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
    : "Just now";

  const trendData = {
    labels: dashboard.months.map((month) => month.label),
    datasets: [{
      label: "Approved hours",
      data: dashboard.months.map((month) => Number(month.hours.toFixed(1))),
      borderColor: BLUE,
      backgroundColor: "rgba(27, 58, 135, 0.10)",
      borderWidth: 2,
      pointBackgroundColor: BLUE,
      pointBorderColor: BLUE,
      pointRadius: 3,
      pointHoverRadius: 4,
      tension: 0.25,
      fill: true,
    }],
  };

  const topProjectData = {
    labels: dashboard.topProjects.map((project) => project.name),
    datasets: [{
      label: "Approved hours",
      data: dashboard.topProjects.map((project) => project.approvedHours),
      backgroundColor: BLUE,
      borderRadius: 2,
      barThickness: 16,
    }],
  };

  const facultyData = {
    labels: dashboard.faculties.map((faculty) => faculty.name),
    datasets: [{
      label: "Students",
      data: dashboard.faculties.map((faculty) => faculty.value),
      backgroundColor: BLUE,
      borderRadius: 2,
      barThickness: 18,
    }],
  };

  return (
    <AppShell>
      <PageHeader
        title="Operations dashboard"
        description={`All-time programme data · Updated ${generatedAt}`}
        actions={
          <>
            <NotificationIcon />
            <Button variant="outline-primary" onClick={() => navigate("/admin/reports")}>
              View reports <FaArrowRight className="ms-2" aria-hidden="true" />
            </Button>
          </>
        }
      />

      <section className="admin-metrics" aria-label="Programme summary">
        <Metric
          label="Approved hours"
          value={formatNumber.format(dashboard.metrics.approvedHours)}
          detail={`${formatInteger.format(dashboard.approvedLogs.length)} approved logs`}
          icon={FaClock}
        />
        <Metric
          label="Pending hours"
          value={formatNumber.format(dashboard.metrics.pendingHours)}
          detail={`${formatInteger.format(dashboard.pendingLogs.length)} logs need review`}
          icon={FaFolderOpen}
        />
        <Metric
          label="Active volunteers"
          value={formatInteger.format(dashboard.metrics.activeVolunteers)}
          detail={`${formatNumber.format(dashboard.engagementRate)}% of ${formatInteger.format(dashboard.metrics.totalStudents)} students`}
          icon={FaUserCheck}
        />
        <Metric
          label="Approved projects"
          value={`${formatInteger.format(dashboard.metrics.approvedProjects)}/${formatInteger.format(dashboard.metrics.totalProjects)}`}
          detail={`${formatNumber.format(dashboard.approvalRate)}% approval rate`}
          icon={FaCheck}
        />
        <Metric
          label="PWA Adoption"
          value={`${pushStats?.adoptionPercentage || 0}%`}
          detail={`${pushStats?.pwaInstalledCount || 0} on PWA • ${pushStats?.pushSubscribedCount || 0} push active`}
          icon={FaMobileAlt}
        />
      </section>

      <Row className="g-4 mb-4">
        <Col xl={8}>
          <section className="admin-dashboard-panel admin-dashboard-panel--chart">
            <SectionHeader
              title="Approved hours over time"
              description="Volunteer hours approved during the last 12 calendar months."
            />
            <div className="admin-chart-summary">
              <strong className="wcco-technical">{formatNumber.format(dashboard.twelveMonthHours)} hrs</strong>
              <span>12-month total</span>
            </div>
            <div className="admin-chart admin-chart--trend">
              <Line options={baseChartOptions} data={trendData} />
            </div>
          </section>
        </Col>
        <Col xl={4}>
          <section className="admin-dashboard-panel admin-dashboard-panel--chart">
            <SectionHeader
              title="Top projects"
              description="Projects ranked by approved volunteer hours."
            />
            {dashboard.topProjects.length ? (
              <div className="admin-chart admin-chart--ranking">
                <Bar options={horizontalChartOptions} data={topProjectData} />
              </div>
            ) : (
              <EmptyState>No approved project hours are available yet.</EmptyState>
            )}
          </section>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xl={7}>
          <section className="admin-dashboard-panel">
            <SectionHeader
              title="Review workload"
              description="Items requiring an administrator or project leader decision."
              action={<span className="wcco-status">{dashboard.pendingProjects.length + dashboard.pendingLogs.length} open items</span>}
            />

            <div className="admin-workload-summary">
              <button type="button" onClick={() => navigate("/admin/projects")}>
                <span>Project approvals</span>
                <strong className="wcco-technical">{dashboard.pendingProjects.length}</strong>
              </button>
              <div>
                <span>Hour logs</span>
                <strong className="wcco-technical">{dashboard.pendingLogs.length}</strong>
              </div>
              <div>
                <span>Hours awaiting review</span>
                <strong className="wcco-technical">{formatNumber.format(dashboard.metrics.pendingHours)}</strong>
              </div>
            </div>

            {dashboard.pendingProjects.length ? (
              <div className="table-responsive admin-review-table">
                <Table hover>
                  <thead>
                    <tr>
                      <th>Project</th>
                      <th>Organiser</th>
                      <th className="text-end">Decision</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboard.pendingProjects.slice(0, 4).map((project) => (
                      <tr key={project._id}>
                        <td>
                          <button className="admin-text-link" type="button" onClick={() => navigate(`/admin/projects/${project._id}`)}>
                            {project.name}
                          </button>
                          <small>{project.categories?.[0] || "No category"}</small>
                        </td>
                        <td>{project.organiserName || "Not specified"}</td>
                        <td>
                          <div className="admin-row-actions">
                            <Button
                              size="sm"
                              disabled={actionProjectId === project._id}
                              onClick={() => handleApproveReject(project._id, true)}
                            >
                              <FaCheck aria-hidden="true" /> Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={actionProjectId === project._id}
                              onClick={() => handleApproveReject(project._id, false)}
                            >
                              <FaTimes aria-hidden="true" /> Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <EmptyState>There are no project proposals awaiting approval.</EmptyState>
            )}
          </section>
        </Col>

        <Col xl={5}>
          <section className="admin-dashboard-panel">
            <SectionHeader
              title="Pending hours by project"
              description="Where the current hour-review backlog is concentrated."
            />
            {dashboard.pendingByProject.length ? (
              <ol className="admin-ranked-list">
                {dashboard.pendingByProject.map((project, index) => (
                  <li key={project.name}>
                    <span className="admin-ranked-list__position wcco-technical">{index + 1}</span>
                    <span className="admin-ranked-list__label">{project.name}</span>
                    <strong className="wcco-technical">{formatNumber.format(project.pendingHours)} hrs</strong>
                  </li>
                ))}
              </ol>
            ) : (
              <EmptyState>No logged hours are awaiting review.</EmptyState>
            )}
          </section>
        </Col>
      </Row>

      <Row className="g-4 mb-4">
        <Col xl={6}>
          <section className="admin-dashboard-panel admin-dashboard-panel--chart">
            <SectionHeader
              title="Student distribution"
              description="Registered student volunteers across the six largest faculties."
            />
            {dashboard.faculties.length ? (
              <div className="admin-chart admin-chart--distribution">
                <Bar options={horizontalChartOptions} data={facultyData} />
              </div>
            ) : (
              <EmptyState>No faculty information is available.</EmptyState>
            )}
          </section>
        </Col>

        <Col xl={6}>
          <section className="admin-dashboard-panel">
            <SectionHeader
              title="Volunteer demand signals"
              description="Most selected volunteering interests among students."
            />
            {dashboard.interests.length ? (
              <ol className="admin-demand-list">
                {dashboard.interests.map((interest) => {
                  const share = dashboard.interestTotal ? (interest.value / dashboard.interestTotal) * 100 : 0;
                  return (
                    <li key={interest.name}>
                      <div>
                        <span>{interest.name}</span>
                        <strong className="wcco-technical">{formatInteger.format(interest.value)} selections</strong>
                      </div>
                      <div className="admin-demand-list__track" aria-hidden="true">
                        <span style={{ width: `${share}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ol>
            ) : (
              <EmptyState>No volunteering-interest data is available.</EmptyState>
            )}
          </section>
        </Col>
      </Row>

      <section className="admin-dashboard-panel">
        <SectionHeader
          title="Recent hour activity"
          description="Latest submitted volunteer-hour records across all projects."
          action={
            <Button variant="link" onClick={() => navigate("/admin/reports")}>
              Open full report <FaArrowRight className="ms-2" aria-hidden="true" />
            </Button>
          }
        />
        {dashboard.logs.length ? (
          <div className="table-responsive">
            <Table hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Volunteer</th>
                  <th>Project</th>
                  <th className="text-end">Hours</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.logs.slice(0, 6).map((log, index) => (
                  <tr key={`${log.studentNumber}-${log.startTime}-${index}`}>
                    <td className="wcco-technical">{new Date(log.startTime).toLocaleDateString("en-ZA")}</td>
                    <td>
                      {log.studentName}
                      <small>{log.studentNumber}</small>
                    </td>
                    <td>{log.projectName}</td>
                    <td className="text-end wcco-technical">{formatNumber.format(log.hours)}</td>
                    <td><span className="wcco-status">{log.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        ) : (
          <EmptyState>No volunteer hours have been submitted.</EmptyState>
        )}
      </section>
    </AppShell>
  );
};

export default AdminDashboard;
